import { Trans, useLingui } from "@lingui/react/macro";
import { patientKeys, updateRelative } from "@/api/Patients";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deducedError } from "@/Utils/ErrorUtils";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Checkbox from "@/components/Checkbox/Checkbox";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import type { IFormRelative, IRelative } from "@medoma/models/patients";
import {
  localizePhoneNumber,
  standardizePhoneNumber,
} from "@medoma/models/phoneNumbers";
import { useUserProfile } from "@/api/Users";
import styles from "./EditRelative.module.scss";

export const EditRelative = ({
  onSuccess,
  onCancel,
  patientId,
  currentRelative,
}: {
  onSuccess: () => void;
  onCancel: () => void;
  patientId: string;
  currentRelative: IRelative;
}) => {
  const { t } = useLingui();
  const { countryCode } = useUserProfile();

  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm<IFormRelative>({
    defaultValues: {
      id: currentRelative.id,
      name: currentRelative.name,
      phoneNumber: currentRelative.phoneNumber
        ? localizePhoneNumber({
            countryCode,
            phoneNumber: currentRelative.phoneNumber,
          })
        : undefined,
      relation: currentRelative.relation,
      isLivingTogether: currentRelative.isLivingTogether,
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (relative: IRelative) => updateRelative(patientId, relative),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  const validateAndSubmit = handleSubmit((formData) => {
    const relative = {
      ...formData,
      id: currentRelative.id,
      phoneNumber: formData.phoneNumber
        ? standardizePhoneNumber({
            countryCode,
            phoneNumber: formData.phoneNumber,
          })
        : null,
    };
    mutate(relative);
  });

  return (
    <Form onSubmit={validateAndSubmit}>
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar anhörig`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <Form.Row>
            <InputField
              label={t`Namn`}
              showOptionalLabel={false}
              errorMessage={errors.name?.message}
              {...register("name", {
                required: {
                  value: true,
                  message: t`Ange den anhörigas namn`,
                },
              })}
            />
            <InputField
              label={t`Telefonnummer`}
              showOptionalLabel={true}
              errorMessage={errors.phoneNumber?.message}
              {...register("phoneNumber", {
                pattern: {
                  value: /^([0-9]|\s)+$/,
                  message: t`Telefonnumret får bara innehålla siffror`,
                },
              })}
            />
            <InputField
              label={t`Relation`}
              showOptionalLabel={false}
              errorMessage={errors.relation?.message}
              {...register("relation", {
                required: {
                  value: true,
                  message: t`Ange relationen till patienten`,
                },
              })}
            />
            <Checkbox
              label={{ text: t`Sammanboende` }}
              errorMessage={errors.isLivingTogether?.message}
              {...register("isLivingTogether")}
            />
          </Form.Row>
          <div className={styles.buttonContainer}>
            <FilledButton type="submit">
              <Trans>Spara ändringar</Trans>
            </FilledButton>
            <PlainButton type="button" onClick={onCancel}>
              <Trans>Avbryt</Trans>
            </PlainButton>
          </div>
        </>
      )}
    </Form>
  );
};
